import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Building2, TrendingUp, Users, FileStack, CreditCard, AlertTriangle,
  CheckCircle2, Loader2, Sparkles, Calendar, Search,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import AiQuotaWidget from "@/components/AiQuotaWidget";

const PLAN_BADGE = {
  starter: "bg-zinc-100 text-zinc-700 border-zinc-200",
  pro: "bg-blue-100 text-blue-700 border-blue-200",
  enterprise: "bg-violet-100 text-violet-700 border-violet-200",
};
const STATUS_BADGE = {
  trial: "bg-amber-50 text-amber-700 border-amber-200",
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  past_due: "bg-red-50 text-red-700 border-red-200",
  canceled: "bg-zinc-100 text-zinc-600 border-zinc-200",
};
const STATUS_LABEL = {
  trial: "Trial",
  active: "Aktiv",
  past_due: "Förfallen",
  canceled: "Uppsagd",
};

function fmtSek(n) {
  return Number(n || 0).toLocaleString("sv-SE", {
    style: "currency", currency: "SEK", maximumFractionDigits: 0,
  });
}

function fmtDate(iso) {
  if (!iso) return "-";
  return new Date(iso).toLocaleDateString("sv-SE", {
    day: "2-digit", month: "short", year: "numeric",
  });
}

function MetricCard({ icon: Icon, label, value, sub, color = "blue" }) {
  const colors = {
    blue: "bg-blue-50 text-blue-700 border-blue-100",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
    violet: "bg-violet-50 text-violet-700 border-violet-100",
    amber: "bg-amber-50 text-amber-700 border-amber-100",
  };
  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-5">
      <div className={`inline-flex items-center justify-center w-9 h-9 ${colors[color]} border rounded-sm`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="mt-4 text-xs font-semibold uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="mt-1 text-3xl font-semibold text-zinc-900 tabular-nums" style={{ fontFamily: "Outfit" }}>
        {value}
      </div>
      {sub && <div className="mt-1.5 text-xs text-zinc-500">{sub}</div>}
    </div>
  );
}

const EMPTY_METRICS = {
  totals: { orgs: 0, active: 0, trial: 0, canceled: 0, past_due: 0, users: 0, cases: 0, cases_this_month: 0 },
  revenue: { mrr_sek: 0, arr_sek: 0, paid_30d_sek: 0, transactions_30d: 0 },
  plan_split: { starter: 0, pro: 0, enterprise: 0 },
  churn: { canceled_30d: 0, rate: 0 },
};

export default function SuperAdmin() {
  const [metrics, setMetrics] = useState(EMPTY_METRICS);
  const [orgs, setOrgs] = useState([]);
  const [recent, setRecent] = useState({ signups: [], payments: [] });
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const loadOrgs = React.useCallback(async () => {
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (q.trim()) params.q = q.trim();
    const { data } = await api.get("/super/orgs", { params });
    setOrgs(data);
  }, [statusFilter, q]);

  useEffect(() => {
    let mounted = true;
    Promise.all([api.get("/super/metrics"), api.get("/super/recent")])
      .then(([m, r]) => {
        if (!mounted) return;
        setMetrics(m.data);
        setRecent(r.data);
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);
  useEffect(() => { loadOrgs(); }, [loadOrgs]);

  const planTotal = (metrics.plan_split.starter || 0) + (metrics.plan_split.pro || 0) + (metrics.plan_split.enterprise || 0) || 1;

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="bg-zinc-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.3em] text-blue-300">
              Plattformsägare · Bygglov SaaS
            </div>
            <h1 className="mt-1 text-2xl font-semibold tracking-tight" style={{ fontFamily: "Outfit" }}>
              Super-admin
            </h1>
          </div>
          <Link to="/login" className="text-xs text-white/60 hover:text-white">Logga ut</Link>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* KPI cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="super-kpis">
          <MetricCard icon={TrendingUp} label="MRR" color="emerald"
            value={fmtSek(metrics.revenue.mrr_sek)}
            sub={`ARR: ${fmtSek(metrics.revenue.arr_sek)}`} />
          <MetricCard icon={CreditCard} label="Intäkt 30d" color="blue"
            value={fmtSek(metrics.revenue.paid_30d_sek)}
            sub={`${metrics.revenue.transactions_30d} transaktioner`} />
          <MetricCard icon={Building2} label="Organisationer" color="violet"
            value={metrics.totals.orgs}
            sub={`${metrics.totals.active} aktiva · ${metrics.totals.trial} trial`} />
          <MetricCard icon={AlertTriangle} label="Churn 30d" color="amber"
            value={`${(metrics.churn.rate * 100).toFixed(1)}%`}
            sub={`${metrics.churn.canceled_30d} uppsagda`} />
        </div>

        {/* AI-quota (live guard status) */}
        <AiQuotaWidget />

        {/* Plan distribution */}
        <div className="bg-white border border-zinc-200 rounded-sm p-6">
          <h2 className="text-lg font-medium" style={{ fontFamily: "Outfit" }}>Plan-fördelning (aktiva)</h2>
          <div className="mt-5 space-y-3">
            {["starter", "pro", "enterprise"].map((pid) => {
              const n = metrics.plan_split[pid] || 0;
              const pct = (n / planTotal) * 100;
              return (
                <div key={pid}>
                  <div className="flex items-center justify-between text-sm mb-1.5">
                    <span className="capitalize font-medium text-zinc-700">{pid}</span>
                    <span className="text-zinc-500 tabular-nums">{n} st · {pct.toFixed(0)}%</span>
                  </div>
                  <div className="h-2 bg-zinc-100 rounded-sm overflow-hidden">
                    <div className={`h-full ${pid === "pro" ? "bg-blue-700" : pid === "enterprise" ? "bg-violet-700" : "bg-zinc-500"}`}
                         style={{ width: `${Math.max(pct, 1.5)}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-6 pt-5 border-t border-zinc-200 grid grid-cols-3 gap-4 text-sm">
            <div>
              <div className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Användare</div>
              <div className="mt-1 text-xl font-semibold tabular-nums">{metrics.totals.users}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Ärenden totalt</div>
              <div className="mt-1 text-xl font-semibold tabular-nums">{metrics.totals.cases}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Ärenden i månaden</div>
              <div className="mt-1 text-xl font-semibold tabular-nums">{metrics.totals.cases_this_month}</div>
            </div>
          </div>
        </div>

        {/* Organisations table */}
        <div className="bg-white border border-zinc-200 rounded-sm">
          <div className="px-6 py-4 flex flex-wrap items-center justify-between gap-3 border-b border-zinc-200">
            <h2 className="text-lg font-medium" style={{ fontFamily: "Outfit" }}>Alla organisationer</h2>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-400" />
                <input value={q} onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && loadOrgs()}
                  placeholder="Sök kommun..." data-testid="super-search"
                  className="pl-8 pr-3 py-1.5 border border-zinc-300 rounded-sm text-sm w-48 focus:border-blue-700 outline-none" />
              </div>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                data-testid="super-status-filter"
                className="px-3 py-1.5 border border-zinc-300 rounded-sm text-sm bg-white">
                <option value="">Alla status</option>
                <option value="active">Aktiva</option>
                <option value="trial">Trial</option>
                <option value="past_due">Förfallna</option>
                <option value="canceled">Uppsagda</option>
              </select>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="super-orgs-table">
              <thead className="bg-zinc-50 text-zinc-600">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-semibold uppercase tracking-wider">Organisation</th>
                  <th className="text-left px-3 py-3 text-xs font-semibold uppercase tracking-wider">Ägare</th>
                  <th className="text-left px-3 py-3 text-xs font-semibold uppercase tracking-wider">Plan</th>
                  <th className="text-left px-3 py-3 text-xs font-semibold uppercase tracking-wider">Status</th>
                  <th className="text-right px-3 py-3 text-xs font-semibold uppercase tracking-wider">MRR</th>
                  <th className="text-right px-3 py-3 text-xs font-semibold uppercase tracking-wider">Platser</th>
                  <th className="text-right px-3 py-3 text-xs font-semibold uppercase tracking-wider">Ärenden/m</th>
                  <th className="text-left px-3 py-3 text-xs font-semibold uppercase tracking-wider">Skapat</th>
                </tr>
              </thead>
              <tbody>
                {orgs.length === 0 ? (
                  <tr><td colSpan={8} className="px-6 py-12 text-center text-zinc-500">Inga organisationer matchar.</td></tr>
                ) : orgs.map((o) => (
                  <tr key={o.org_id} className="border-t border-zinc-100 hover:bg-zinc-50">
                    <td className="px-6 py-3">
                      <div className="font-medium text-zinc-900">{o.name}</div>
                      <div className="text-xs text-zinc-500">{o.slug}</div>
                    </td>
                    <td className="px-3 py-3 text-zinc-700">
                      {o.owner ? (<><div>{o.owner.name}</div><div className="text-xs text-zinc-500">{o.owner.email}</div></>) : "-"}
                    </td>
                    <td className="px-3 py-3">
                      <span className={`px-2 py-0.5 text-[10px] font-semibold uppercase rounded-sm border ${PLAN_BADGE[o.plan] || ""}`}>{o.plan}</span>
                    </td>
                    <td className="px-3 py-3">
                      <span className={`px-2 py-0.5 text-[10px] font-semibold uppercase rounded-sm border ${STATUS_BADGE[o.status] || ""}`}>
                        {STATUS_LABEL[o.status] || o.status}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums font-medium">{fmtSek(o.mrr_sek)}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-zinc-700">{o.seats_used}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-zinc-700">{o.cases_this_month}</td>
                    <td className="px-3 py-3 text-zinc-500 text-xs">{fmtDate(o.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent activity */}
        <div className="grid lg:grid-cols-2 gap-5">
          <div className="bg-white border border-zinc-200 rounded-sm p-6" data-testid="recent-signups">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-4 h-4 text-blue-700" />
              <h3 className="font-medium" style={{ fontFamily: "Outfit" }}>Nya kunder</h3>
            </div>
            <ul className="space-y-3">
              {recent.signups.slice(0, 10).map((s) => (
                <li key={s.org_id} className="flex items-center justify-between gap-3 py-2 border-b border-zinc-100 last:border-0">
                  <div className="min-w-0">
                    <div className="font-medium text-sm text-zinc-900 truncate">{s.name}</div>
                    <div className="text-xs text-zinc-500 flex items-center gap-1.5">
                      <Calendar className="w-3 h-3" /> {fmtDate(s.created_at)}
                    </div>
                  </div>
                  <span className={`px-2 py-0.5 text-[10px] font-semibold uppercase rounded-sm border ${STATUS_BADGE[s.status] || ""}`}>
                    {STATUS_LABEL[s.status] || s.status}
                  </span>
                </li>
              ))}
              {recent.signups.length === 0 && (
                <li className="text-sm text-zinc-500 text-center py-6">Inga nya kunder än.</li>
              )}
            </ul>
          </div>

          <div className="bg-white border border-zinc-200 rounded-sm p-6" data-testid="recent-payments">
            <div className="flex items-center gap-2 mb-4">
              <CreditCard className="w-4 h-4 text-emerald-600" />
              <h3 className="font-medium" style={{ fontFamily: "Outfit" }}>Senaste betalningar</h3>
            </div>
            <ul className="space-y-3">
              {recent.payments.slice(0, 10).map((p) => (
                <li key={p.session_id} className="flex items-center justify-between gap-3 py-2 border-b border-zinc-100 last:border-0">
                  <div className="min-w-0">
                    <div className="font-medium text-sm text-zinc-900 truncate">{p.org_name}</div>
                    <div className="text-xs text-zinc-500 flex items-center gap-1.5">
                      <CheckCircle2 className="w-3 h-3 text-emerald-500" /> {p.plan_id} · {fmtDate(p.updated_at)}
                    </div>
                  </div>
                  <span className="text-sm font-semibold text-emerald-700 tabular-nums">{fmtSek(p.amount)}</span>
                </li>
              ))}
              {recent.payments.length === 0 && (
                <li className="text-sm text-zinc-500 text-center py-6">Inga betalningar än.</li>
              )}
            </ul>
          </div>
        </div>

        <HandlaggareWhitelistManager />
      </div>
    </div>
  );
}


// ---------- Whitelist manager för /handlaggare-portalen ----------
function HandlaggareWhitelistManager() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState("");
  const [newNote, setNewNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/super/handlaggare-whitelist");
      setList(data);
    } catch (e) {
      // noop
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!newEmail.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post("/super/handlaggare-whitelist", { email: newEmail.trim(), note: newNote.trim() });
      const invite = data.invite_email;
      if (invite?.sent) {
        toast.success(`${newEmail} tillagd + inbjudan skickad`);
      } else if (invite?.error?.includes("verify a domain")) {
        toast.warning(`${newEmail} tillagd, men inbjudan kunde inte skickas (Resend testmode – verifiera domän för att skicka till alla)`, { duration: 8000 });
      } else if (invite?.error) {
        toast.warning(`${newEmail} tillagd, men inbjudan kunde inte skickas: ${invite.error.slice(0, 60)}`, { duration: 6000 });
      } else {
        toast.success(`${newEmail} tillagd`);
      }
      setNewEmail("");
      setNewNote("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunde inte lägga till");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (email, is_active) => {
    try {
      await api.patch(`/super/handlaggare-whitelist/${encodeURIComponent(email)}`, { is_active });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunde inte uppdatera");
    }
  };

  const remove = async (email) => {
    if (!window.confirm(`Ta bort ${email} från whitelisten? Åtkomst till /handlaggare stängs omedelbart.`)) return;
    try {
      await api.delete(`/super/handlaggare-whitelist/${encodeURIComponent(email)}`);
      toast.success("Borttagen");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunde inte ta bort");
    }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-6" data-testid="whitelist-manager">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-zinc-900" style={{ fontFamily: "Outfit" }}>
            Handläggare-portal · Whitelist
          </h2>
          <p className="text-xs text-zinc-500 mt-1">
            Endast e-postadresser här kan logga in via <code className="text-zinc-700">/handlaggare</code>. Länken visas aldrig publikt.
          </p>
        </div>
        <span className="inline-flex items-center px-2 py-0.5 text-[11px] uppercase tracking-wider bg-blue-50 text-blue-800 border border-blue-200 rounded-sm">
          {list.length} {list.length === 1 ? "post" : "poster"}
        </span>
      </div>

      <form onSubmit={add} className="grid grid-cols-1 md:grid-cols-6 gap-2 mb-4 pb-4 border-b border-zinc-200">
        <input
          type="email"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          placeholder="handlaggare@kommun.se"
          required
          data-testid="whitelist-new-email"
          className="md:col-span-2 px-3 py-2 border border-zinc-300 rounded-sm text-sm outline-none focus:border-blue-700"
        />
        <input
          type="text"
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          placeholder="Anteckning (valfri) t.ex. 'Stockholm stad'"
          data-testid="whitelist-new-note"
          className="md:col-span-3 px-3 py-2 border border-zinc-300 rounded-sm text-sm outline-none focus:border-blue-700"
        />
        <button
          type="submit"
          disabled={saving || !newEmail}
          data-testid="whitelist-add-btn"
          className="inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white text-sm rounded-sm disabled:opacity-60"
        >
          Lägg till
        </button>
      </form>

      {loading ? (
        <p className="text-sm text-zinc-500 py-4 text-center">Laddar…</p>
      ) : list.length === 0 ? (
        <p className="text-sm text-zinc-500 py-4 text-center">Whitelisten är tom.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-zinc-500">
                <th className="text-left pb-2 pr-3">E-post</th>
                <th className="text-left pb-2 pr-3">Anteckning</th>
                <th className="text-left pb-2 pr-3">Tillagd</th>
                <th className="text-left pb-2 pr-3">Senast inne</th>
                <th className="text-left pb-2 pr-3">Loggins</th>
                <th className="text-left pb-2 pr-3">Status</th>
                <th className="text-right pb-2">Åtgärder</th>
              </tr>
            </thead>
            <tbody>
              {list.map((row) => (
                <tr key={row.email} className="border-t border-zinc-100" data-testid={`whitelist-row-${row.email}`}>
                  <td className="py-2 pr-3 font-mono text-xs">{row.email}</td>
                  <td className="py-2 pr-3 text-zinc-600 text-xs">
                    <div>{row.note || "—"}</div>
                    {row.invite_email && (
                      row.invite_email.sent
                        ? <span className="inline-flex items-center px-1.5 py-0.5 text-[9px] bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-sm mt-1">✉ Inbjudan skickad</span>
                        : <span className="inline-flex items-center px-1.5 py-0.5 text-[9px] bg-amber-50 text-amber-800 border border-amber-200 rounded-sm mt-1" title={row.invite_email.error}>✉ Inbjudan ej skickad</span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-xs text-zinc-500">
                    {row.added_at ? new Date(row.added_at).toLocaleDateString("sv-SE") : "—"}
                    <div className="text-[10px] text-zinc-400">{row.added_by}</div>
                  </td>
                  <td className="py-2 pr-3 text-xs text-zinc-500">
                    {row.last_login_at ? new Date(row.last_login_at).toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "short" }) : "Aldrig"}
                  </td>
                  <td className="py-2 pr-3 text-xs tabular-nums">{row.login_count || 0}</td>
                  <td className="py-2 pr-3">
                    {row.is_active ? (
                      <span className="inline-flex items-center px-2 py-0.5 text-[10px] bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-sm">Aktiv</span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 text-[10px] bg-zinc-100 text-zinc-500 border border-zinc-200 rounded-sm">Inaktiverad</span>
                    )}
                  </td>
                  <td className="py-2 text-right space-x-2">
                    <button
                      onClick={() => toggle(row.email, !row.is_active)}
                      data-testid={`whitelist-toggle-${row.email}`}
                      className="text-xs text-blue-700 hover:underline"
                    >
                      {row.is_active ? "Inaktivera" : "Aktivera"}
                    </button>
                    <button
                      onClick={() => remove(row.email)}
                      data-testid={`whitelist-delete-${row.email}`}
                      className="text-xs text-red-600 hover:underline"
                    >
                      Ta bort
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
